import React, { useEffect, useRef, useState } from "react";
import {
  Search,
  Calendar,
  PlaneTakeoff,
  PlaneLanding,
  ArrowUpDown,
  Sparkles,
  Users,
  Compass,
  TrendingUp,
  Info,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { getAllConfigsList } from "../../../services/Admin/commonService";
import { decryptData } from "../../../utils/crypto";
import { notify } from "../../../components/Common/Alerts";
import Utils from "../../../utils";
import Select from "react-select";
import { getAirportsSerachList } from "../../../services/Front/flightService";
import { useSearch } from "../../../context/SearchContext";

const AnalyzeSearch = () => {
  const navigate = useNavigate();
  const returnDateRef = useRef(null);
  const departureRef = useRef(null);
  const [tripTypeList, setTripTypeList] = useState([]);
  const [cabinClassList, setCabinClassList] = useState([]);
  const [loading, setLoading] = useState(false);

  const [originList, setOriginList] = useState([]);
  const [destinationList, setDestinationList] = useState([]);
  const [originKeyword, setOriginKeyword] = useState("");
  const [destinationKeyword, setDestinationKeyword] = useState("");
  const [btnLoading, setBtnLoading] = useState(false);
  const originRef = useRef(null);
  const destinationRef = useRef(null);
  const [isSelecting, setIsSelecting] = useState(false);
  const { formData, setFormData } = useSearch();
  const [isTypingOrigin, setIsTypingOrigin] = useState(false);
  const [isTypingDestination, setIsTypingDestination] = useState(false);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (originRef.current && !originRef.current.contains(event.target)) {
        setOriginList([]);
      }
      if (
        destinationRef.current &&
        !destinationRef.current.contains(event.target)
      ) {
        setDestinationList([]);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (!isTypingOrigin) return;
    if (isSelecting) return;

    const q = originKeyword.replace(/\([A-Z]{3}\)/, "").trim();
    if (!q || q.length < 2) return;

    const delay = setTimeout(() => {
      getAirportsSerachList({ keyword: q }).then((response) => {
        const decrypted = decryptData(response.data.data);

        const cleaned = (decrypted || [])
          .filter((item) => item.code?.length === 3)
          .filter((item) => item.code !== formData.destination_code);

        setOriginList(cleaned);
      });
    }, 300);

    return () => clearTimeout(delay);
  }, [originKeyword]);

  useEffect(() => {
    if (!isTypingDestination) return;
    if (isSelecting) return;

    const q = destinationKeyword.replace(/\([A-Z]{3}\)/, "").trim();
    if (!q || q.length < 2) return;

    const delay = setTimeout(() => {
      getAirportsSerachList({ keyword: q }).then((response) => {
        const decrypted = decryptData(response.data.data);

        const cleaned = (decrypted || [])
          .filter((item) => item.code?.length === 3)
          .filter((item) => item.code !== formData.origin_code);

        setDestinationList(cleaned);
      });
    }, 300);

    return () => clearTimeout(delay);
  }, [destinationKeyword]);

  useEffect(() => {
    setIsTypingOrigin(false);
    setIsTypingDestination(false);
    setOriginList([]);
    setDestinationList([]);
  }, []);

  useEffect(() => {
    if (!formData?.origin_code) return;
    setIsTypingOrigin(false);

    setOriginKeyword(`${formData.origin} (${formData.origin_code})`);
  }, [formData.origin_code]);

  useEffect(() => {
    if (!formData?.destination_code) return;
    setIsTypingDestination(false);

    setDestinationKeyword(
      `${formData.destination} (${formData.destination_code})`,
    );
  }, [formData.destination_code]);

  useEffect(() => {
    if (formData?.trip_type === "oneway") {
      setFormData((prev) => ({ ...prev, return_date: "" }));
    }
  }, [formData?.trip_type]);

  useEffect(() => {
    getAllConfigsList()
      .then((response) => {
        const decrypted = decryptData(response.data.data);
        setTripTypeList(
          decrypted.data.trip_type?.map((i) => ({
            value: i.value,
            label: i.label,
          })) || [],
        );
        setCabinClassList(
          decrypted.data.cabin_class?.map((i) => ({
            value: i.value,
            label: i.label,
          })) || [],
        );
      })
      .catch((err) => notify.error(Utils.getErrorMessage(err)));
  }, []);

  useEffect(() => {
    if (
      formData.return_date &&
      formData.departure_date &&
      formData.return_date < formData.departure_date
    ) {
      setFormData((prev) => ({
        ...prev,
        return_date: "",
      }));

      // focus return date field
      setTimeout(() => {
        returnDateRef.current?.focus();
      }, 100);
    }
  }, [formData.return_date, formData.departure_date]);

  const swapRoute = () => {
    if (!formData.origin_code && !formData.destination_code) return;

    setFormData((prev) => ({
      ...prev,
      origin: prev.destination,
      origin_code: prev.destination_code,
      destination: prev.origin,
      destination_code: prev.origin_code,
    }));

    setOriginKeyword(destinationKeyword);
    setDestinationKeyword(originKeyword);
  };

  const originDisplay =
    formData.origin && formData.origin_code
      ? `${formData.origin} (${formData.origin_code})`
      : "";

  const destinationDisplay =
    formData?.destination && formData?.destination_code
      ? `${formData.destination} (${formData.destination_code})`
      : "";

  const handleChange = (e) => {
    const { name, value } = e.target;
    let updated = { ...formData, [name]: value };

    if (name === "departure_date") {
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      const departureDate = new Date(value);
      departureDate.setHours(0, 0, 0, 0);

      // Departure date cannot be in the past
      if (departureDate < today) {
        notify.error("Departure date cannot be in the past");
        return;
      }

      // If return date is empty, set it to departure date
      if (!formData.return_date) {
        updated.return_date = value;
      } else {
        const returnDate = new Date(formData.return_date);
        returnDate.setHours(0, 0, 0, 0);
//this is commented code
        // If return date is before departure, update it
        // if (returnDate < departureDate) {
        //   updated.return_date = value;
        //   notify.error("Return date updated to match the departure date.");
        // }
      }
    }

    if (name === "return_date") {
      if (formData.departure_date) {
        const departureDate = new Date(formData.departure_date);
        departureDate.setHours(0, 0, 0, 0);

        const returnDate = new Date(value);
        returnDate.setHours(0, 0, 0, 0);

        if (returnDate < departureDate) {
          notify.error(
            "Return date cannot be earlier than the departure date.",
          );
          return;
        }
      }
    }

    setFormData(updated);
  };

  const handleSearch = async () => {
    if (!formData?.origin_code) {
      return notify.error("Please select origin");
    }

    if (!formData?.destination_code) {
      return notify.error("Please select destination");
    }

    if (!formData?.departure_date) {
      return notify.error("Please select departure date");
    }

    if (formData?.trip_type === "roundtrip" && !formData?.return_date) {
      return notify.error("Please select return date");
    }

    try {
      setBtnLoading(true);

      await new Promise((resolve) => setTimeout(resolve, 700));

      navigate("/card-select");
    } catch (error) {
      console.log(error);
    } finally {
      setBtnLoading(false);
    }
  };

  const getSelectedOption = (list, value) =>
    list.find((opt) => String(opt.value) === String(value)) || null;

  const reactSelectStyles = {
    control: (base) => ({
      ...base,
      background: "transparent",
      border: "none",
      boxShadow: "none",
      minHeight: "24px",
      cursor: "pointer",
    }),
    valueContainer: (base) => ({ ...base, padding: "0px" }),
    input: (base) => ({
      ...base,
      margin: "0px",
      color: "inherit",
      fontWeight: "700",
      fontSize: "14px",
    }),
    singleValue: (base) => ({
      ...base,
      color: "inherit",
      fontWeight: "700",
      fontSize: "14px",
    }),
    placeholder: (base) => ({
      ...base,
      color: "#94A3B8",
      fontSize: "14px",
      fontWeight: "500",
    }),
    indicatorsContainer: (base) => ({ ...base, display: "none" }),
    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
  };

  return (
    <div className="w-full bg-[#F8FAFC] dark:bg-[#070C19] text-slate-800 dark:text-slate-100 font-sans antialiased transition-colors duration-300">
      <main className="w-full max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8 xl:px-12 py-6 lg:py-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 xl:gap-8 items-start">
          <div className="lg:col-span-8 space-y-6">
            <div className="relative rounded-3xl bg-gradient-to-br from-[#CAA036] via-[#B8860B] to-[#8B6508] p-6 sm:p-8 md:p-10 text-white overflow-hidden shadow-lg border border-amber-600/10">
              <div className="absolute right-0 top-0 bottom-0 w-1/3 opacity-10 pointer-events-none hidden md:block">
                <Compass
                  size={240}
                  className="text-white absolute -right-10 -bottom-10"
                />
              </div>
              <div className="relative z-10 space-y-4">
                <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/10 border border-white/20 backdrop-blur-md">
                  <Sparkles
                    size={13}
                    className="text-amber-200 fill-amber-200"
                  />
                  <span className="text-[10px] font-bold tracking-wide text-amber-100 uppercase">
                    Redemption Engine
                  </span>
                </div>
                <h1 className="text-2xl sm:text-3xl md:text-4xl font-extrabold tracking-tight leading-tight">
                  Where to <span className="text-amber-200">Next?</span>
                </h1>
                <p className="text-xs sm:text-sm font-normal text-amber-50/80 max-w-xl leading-relaxed">
                  Scan live award flight inventory channels across strategic
                  alliances to isolate peak valuation points.
                </p>
              </div>
            </div>

            <div className="bg-white dark:bg-[#0B1222] border border-slate-200/60 dark:border-slate-800/60 p-6 sm:p-8 rounded-2xl shadow-sm space-y-6">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div className="group rounded-xl border border-slate-200 dark:border-slate-800 bg-slate-50/50 dark:bg-[#0F172A]/40 p-3.5 flex flex-col justify-center focus-within:border-[#B8860B] focus-within:bg-white dark:focus-within:bg-[#0F172A] transition-all">
                  <span className="block text-[10px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-wider mb-1">
                    Trip Type
                  </span>
                  <Select
                    options={tripTypeList}
                    value={getSelectedOption(tripTypeList, formData.trip_type)}
                    onChange={(selected) =>
                      setFormData((prev) => ({
                        ...prev,
                        trip_type: String(selected?.value ?? ""),
                      }))
                    }
                    placeholder="Select Type"
                    isSearchable={false}
                    menuPortalTarget={document.body}
                    styles={reactSelectStyles}
                  />
                </div>

                <div className="group rounded-xl border border-slate-200 dark:border-slate-800 bg-slate-50/50 dark:bg-[#0F172A]/40 p-3.5 flex flex-col justify-center focus-within:border-[#B8860B] focus-within:bg-white dark:focus-within:bg-[#0F172A] transition-all">
                  <span className="block text-[10px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-wider mb-1">
                    Cabin Class
                  </span>
                  <Select
                    options={cabinClassList}
                    value={getSelectedOption(
                      cabinClassList,
                      formData.cabin_class,
                    )}
                    onChange={(selected) =>
                      setFormData((prev) => ({
                        ...prev,
                        cabin_class: String(selected?.value ?? ""),
                      }))
                    }
                    placeholder="Select Cabin"
                    isSearchable={false}
                    menuPortalTarget={document.body}
                    styles={reactSelectStyles}
                  />
                </div>

                <div className="group rounded-xl border border-slate-200 dark:border-slate-800 bg-slate-50/50 dark:bg-[#0F172A]/40 p-3.5 flex items-center gap-3 focus-within:border-[#B8860B] focus-within:bg-white dark:focus-within:bg-[#0F172A] transition-all">
                  <Users
                    size={16}
                    className="text-slate-400 dark:text-slate-500 shrink-0 group-focus-within:text-[#B8860B]"
                  />
                  <div className="flex-1 min-w-0">
                    <span className="block text-[10px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-wider leading-none">
                      Passengers
                    </span>
                    <input
                      type="text"
                      inputMode="numeric"
                      placeholder="1"
                      className="w-full bg-transparent border-none outline-none mt-1 text-sm font-bold text-slate-900 dark:text-white placeholder-slate-400 p-0 h-5"
                      name="passengers"
                      value={formData?.passengers || ""}
                      onChange={handleChange}
                      maxLength={2}
                    />
                  </div>
                </div>
              </div>

              <div className="relative grid grid-cols-1 md:grid-cols-2 gap-4 items-center">
                <div
                  ref={originRef}
                  className="relative group rounded-xl border border-slate-200 dark:border-slate-800 bg-slate-50/50 dark:bg-[#0F172A]/40 p-4 flex items-center gap-4 focus-within:border-[#B8860B] focus-within:bg-white dark:focus-within:bg-[#0F172A] transition-all"
                >
                  <PlaneTakeoff
                    size={18}
                    className="text-slate-400 dark:text-slate-500 shrink-0 group-focus-within:text-[#B8860B]"
                  />
                  <div className="flex-1 min-w-0">
                    <span className="block text-[10px] font-bold uppercase tracking-wider text-slate-400 dark:text-slate-500">
                      Origin Airport
                    </span>
                    <input
                      type="text"
                      placeholder="Departure city or code (e.g., JFK)"
                      value={originKeyword || originDisplay}
                      onChange={(e) => {
                        setOriginKeyword(e.target.value);
                        setIsTypingOrigin(true);
                      }}
                      className="w-full bg-transparent border-none outline-none mt-1 text-sm font-bold text-slate-900 dark:text-white placeholder-slate-400 p-0 h-5"
                    />
                  </div>

                  {originList?.length > 0 && (
                    <div className="absolute left-0 right-0 top-full mt-2 z-50 bg-white dark:bg-[#0F172A] border border-slate-200 dark:border-slate-800 rounded-xl shadow-xl max-h-64 overflow-y-auto divide-y divide-slate-100 dark:divide-slate-800/60 p-1">
                      {originList.map((item, index) => (
                        <div
                          key={index}
                          onClick={() => {
                            setIsSelecting(true);

                            setFormData({
                              ...formData,
                              origin: item.name,
                              origin_code: item.code,
                            });

                            setOriginKeyword(`${item.city} (${item.code})`);
                            setOriginList([]);

                            setTimeout(() => setIsSelecting(false), 300);
                          }}
                          className="p-2.5 hover:bg-slate-50 dark:hover:bg-slate-800/60 rounded-lg cursor-pointer flex items-center justify-between gap-3"
                        >
                          <div className="min-w-0">
                            <p className="font-bold text-xs text-slate-800 dark:text-slate-200 truncate">
                              {item.city}
                            </p>
                            <p className="text-[11px] text-slate-400 truncate">
                              {item.name}
                            </p>
                          </div>
                          <span className="bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400 px-1.5 py-0.5 rounded text-xs font-mono font-bold shrink-0">
                            {item.code}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-20">
                  <button
                    type="button"
                    onClick={swapRoute}
                    className="w-10 h-10 rounded-full 
                     bg-white dark:bg-[#1E293B] 
                     border border-slate-200 dark:border-slate-700 
                     text-slate-500 hover:text-[#B8860B] 
                     hover:border-amber-500/30 
                     shadow-md hover:shadow-lg
                     transition-all duration-200 
                     active:scale-95 
                     flex items-center justify-center group"
                  >
                    <ArrowUpDown
                      size={16}
                      className="rotate-90 group-hover:rotate-[270deg] transition-transform duration-300"
                    />
                  </button>
                </div>

                <div
                  ref={destinationRef}
                  className="relative group rounded-xl border border-slate-200 dark:border-slate-800 bg-slate-50/50 dark:bg-[#0F172A]/40 p-4 flex items-center gap-4 focus-within:border-[#B8860B] focus-within:bg-white dark:focus-within:bg-[#0F172A] transition-all"
                >
                  <PlaneLanding
                    size={18}
                    className="text-slate-400 dark:text-slate-500 shrink-0 group-focus-within:text-[#B8860B]"
                  />
                  <div className="flex-1 min-w-0">
                    <span className="block text-[10px] font-bold uppercase tracking-wider text-slate-400 dark:text-slate-500">
                      Destination Airport
                    </span>
                    <input
                      type="text"
                      placeholder="Arrival city or code (e.g., LHR)"
                      value={destinationKeyword || destinationDisplay}
                      onChange={(e) => {
                        setDestinationKeyword(e.target.value);
                        setIsTypingDestination(true);
                      }}
                      className="w-full bg-transparent border-none outline-none mt-1 text-sm font-bold text-slate-900 dark:text-white placeholder-slate-400 p-0 h-5"
                    />
                  </div>

                  {destinationList?.length > 0 && (
                    <div className="absolute left-0 right-0 top-full mt-2 z-50 bg-white dark:bg-[#0F172A] border border-slate-200 dark:border-slate-800 rounded-xl shadow-xl max-h-64 overflow-y-auto divide-y divide-slate-100 dark:divide-slate-800/60 p-1">
                      {destinationList.map((item, index) => (
                        <div
                          key={index}
                          onClick={() => {
                            setIsSelecting(true);

                            setFormData({
                              ...formData,
                              destination: item.name,
                              destination_code: item.code,
                            });

                            setDestinationKeyword(
                              `${item.city} (${item.code})`,
                            );
                            setDestinationList([]);

                            setTimeout(() => setIsSelecting(false), 300);
                          }}
                          className="p-2.5 hover:bg-slate-50 dark:hover:bg-slate-800/60 rounded-lg cursor-pointer flex items-center justify-between gap-3"
                        >
                          <div className="min-w-0">
                            <p className="font-bold text-xs text-slate-800 dark:text-slate-200 truncate">
                              {item.city}
                            </p>
                            <p className="text-[11px] text-slate-400 truncate">
                              {item.name}
                            </p>
                          </div>
                          <span className="bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400 px-1.5 py-0.5 rounded text-xs font-mono font-bold shrink-0">
                            {item.code}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              <div
                className={`grid grid-cols-1 gap-4 ${formData?.trip_type === "roundtrip" ? "sm:grid-cols-2" : "grid-cols-1"}`}
              >
                <div className="group rounded-xl border border-slate-200 dark:border-slate-800 bg-slate-50/50 dark:bg-[#0F172A]/40 p-4 flex items-center gap-4 focus-within:border-[#B8860B] focus-within:bg-white dark:focus-within:bg-[#0F172A] transition-all relative">
                  <Calendar
                    size={16}
                    className="text-slate-400 dark:text-slate-500 shrink-0 group-focus-within:text-[#B8860B]"
                  />
                  <div className="flex-1 min-w-0">
                    <span className="block text-[10px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-wider mb-0.5">
                      Departure Date
                    </span>
                    <input
                      ref={departureRef}
                      type="date"
                      className="w-full bg-transparent border-none outline-none mt-1 text-sm font-bold text-slate-900 dark:text-white p-0 h-5 cursor-pointer relative z-10
                      [&::-webkit-calendar-picker-indicator]:absolute [&::-webkit-calendar-picker-indicator]:inset-0 [&::-webkit-calendar-picker-indicator]:w-full [&::-webkit-calendar-picker-indicator]:h-full [&::-webkit-calendar-picker-indicator]:opacity-0"
                      name="departure_date"
                      value={formData?.departure_date || ""}
                      onChange={(e) => {
                        handleChange(e);

                        if (formData.trip_type === "roundtrip") {
                          setTimeout(() => {
                            returnDateRef.current?.focus();
                          }, 150);
                        }
                      }}
                      min={new Date().toISOString().split("T")[0]}
                    />
                  </div>
                </div>

                {formData?.trip_type === "roundtrip" && (
                  <div className="group rounded-xl border border-slate-200 dark:border-slate-800 bg-slate-50/50 dark:bg-[#0F172A]/40 p-4 flex items-center gap-4 focus-within:border-[#B8860B] focus-within:bg-white dark:focus-within:bg-[#0F172A] transition-all relative">
                    <Calendar
                      size={16}
                      className="text-slate-400 dark:text-slate-500 shrink-0 group-focus-within:text-[#B8860B]"
                    />
                    <div className="flex-1 min-w-0">
                      <span className="block text-[10px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-wider mb-0.5">
                        Return Date
                      </span>
                      <input
                        ref={returnDateRef}
                        type="date"
                        name="return_date"
                        value={formData?.return_date || ""}
                        onChange={handleChange}
                        min={
                          formData.departure_date ||
                          new Date().toISOString().split("T")[0]
                        }
                        className="w-full bg-transparent border-none outline-none mt-1 text-sm font-bold text-slate-900 dark:text-white p-0 h-5 cursor-pointer relative z-10
                       [&::-webkit-calendar-picker-indicator]:absolute [&::-webkit-calendar-picker-indicator]:inset-0 [&::-webkit-calendar-picker-indicator]:w-full [&::-webkit-calendar-picker-indicator]:h-full [&::-webkit-calendar-picker-indicator]:opacity-0"
                      />
                    </div>
                  </div>
                )}
              </div>

              <div className="pt-4">
                <button
                  onClick={handleSearch}
                  disabled={btnLoading}
                  className="w-full h-14 rounded-xl 
                   bg-gradient-to-r from-[#CAA036] via-[#B8860B] to-[#9A721C] 
                   hover:from-[#B8860B] hover:to-[#8B6508]
                   disabled:opacity-70 disabled:cursor-not-allowed
                   active:scale-[0.995] text-white font-extrabold text-base 
                   tracking-wide flex items-center justify-center gap-3 
                   shadow-lg shadow-amber-500/10 hover:shadow-xl 
                   hover:shadow-amber-600/20 transition-all duration-200 group/btn"
                >
                  {btnLoading ? (
                    <>
                      <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      <span>Searching Flights...</span>
                    </>
                  ) : (
                    <>
                      <Search
                        size={18}
                        className="stroke-[3] group-hover/btn:scale-110 transition-transform"
                      />
                      <span>Find Reward Flights</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>

          <div className="lg:col-span-4 space-y-4 lg:sticky lg:top-6">
            <div className="bg-white dark:bg-[#0B1222] border border-slate-200/60 dark:border-slate-800/60 p-5 rounded-2xl shadow-sm space-y-4">
              <div className="flex items-center gap-2 pb-3 border-b border-slate-100 dark:border-slate-800/80">
                <TrendingUp size={16} className="text-[#CAA036]" />
                <h2 className="text-xs font-bold uppercase tracking-wider text-slate-900 dark:text-white">
                  Redemption Optimization
                </h2>
              </div>
              <div className="space-y-3.5">
                <div className="p-3.5 rounded-xl bg-slate-50/80 dark:bg-[#0F172A]/40 border border-slate-200/40 dark:border-slate-800/40 flex items-start gap-3">
                  <Info size={14} className="text-[#B8860B] mt-0.5 shrink-0" />
                  <p className="text-xs text-slate-500 dark:text-slate-400 leading-relaxed">
                    Our platform scans inventory availability across multiple
                    program points networks synchronously. Fill in your route
                    parameters to initialize high-value conversion matching.
                  </p>
                </div>
                <div className="grid grid-cols-2 gap-2.5">
                  <div className="p-3 bg-amber-500/5 border border-amber-500/10 rounded-xl text-center">
                    <p className="text-[10px] font-bold text-slate-400 dark:text-slate-500 uppercase">
                      PARTNERS
                    </p>
                    <p className="text-lg font-black text-[#B8860B] dark:text-amber-400 mt-0.5">
                      140+
                    </p>
                  </div>
                  <div className="p-3 bg-emerald-500/5 border border-emerald-500/10 rounded-xl text-center">
                    <p className="text-[10px] font-bold text-slate-400 dark:text-slate-500 uppercase">
                      MIN RATE
                    </p>
                    <p className="text-lg font-black text-emerald-600 dark:text-emerald-400 mt-0.5">
                      1:1
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-r from-slate-900 to-slate-800 dark:from-[#0F172A] dark:to-[#1E293B] border border-slate-800 dark:border-slate-700 p-4 rounded-xl text-white flex items-center justify-between gap-4 shadow-sm">
              <div className="space-y-1">
                <p className="text-[10px] font-bold text-amber-400 uppercase tracking-wider">
                  Alliance Coverage Active
                </p>
                <p className="text-xs text-slate-300 font-medium leading-normal">
                  Star Alliance, SkyTeam, and Oneworld rates loaded.
                </p>
              </div>
              <Compass size={24} className="text-slate-600 shrink-0" />
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default AnalyzeSearch;
